import type { FetchQuoteParams } from 'actions/InsuranceActions'
import { createSelector } from 'reselect'
import {
  getCheckoutInsuranceDates,
  getCheckoutInsuranceBookingProtectionTravellers,
  getCheckoutInsuranceTravellers,
  getCheckoutInsuranceCountries,
} from './insurance'
import getCheckoutInsuranceCoverAmount from './getCheckoutInsuranceCoverAmount'
import { getInsuranceItemByProductType } from '../view/insurance'
import { isCruiseCheckout } from '../view/cruise'

/**
 * Retrieves the list of parameters to fetch a quote that can be derived
 * from the checkout state, this includes:
 * - Dates
 * - Travellers
 * - Coverage amount
 * - Destination countries
 */
function buildCheckoutInsuranceQuoteParamsSelector(
  getTravellers: (state: App.State) => App.Occupants,
  getInsuranceItem: (state: App.State) => App.Checkout.InsuranceItem | undefined,
) {
  return createSelector(
    (state: App.State) => getCheckoutInsuranceDates(state),
    (state: App.State) => getTravellers(state),
    (state: App.State) => getCheckoutInsuranceCoverAmount(state),
    (state: App.State) => getCheckoutInsuranceCountries(state),
    (state: App.State) => state.checkout.cart.cartId,
    (state: App.State) => state.checkout.cart.currencyCode,
    (state: App.State) => getInsuranceItem(state),
    (state: App.State) => isCruiseCheckout(state),
    (dates, travellers, coverAmount, destinationCountries, cartId, currency, item, isCruise): Partial<FetchQuoteParams> => {
      return {
        startDate: dates?.startDate,
        endDate: dates?.endDate,
        travellers,
        coverAmount,
        isCruise,
        cartId,
        currency,
        destinationCountries,
        numberOfSeniors: item?.numberOfSeniors ?? 0,
      }
    },
  )
}

const getCheckoutInsuranceQuoteParams = buildCheckoutInsuranceQuoteParamsSelector(
  getCheckoutInsuranceTravellers,
  (state: App.State) => getInsuranceItemByProductType(state, 'travel-insurance'),
)

export const getCheckoutInsuranceBookingProtectionQuoteParams = buildCheckoutInsuranceQuoteParamsSelector(
  getCheckoutInsuranceBookingProtectionTravellers,
  (state: App.State) => getInsuranceItemByProductType(state, 'booking-protection'),
)

export function getCheckoutInsuranceQuoteParamsForType(
  quoteParams: Partial<FetchQuoteParams>,
  bookingProtectionQuoteParams: Partial<FetchQuoteParams>,
  type: App.InsuranceProductType | undefined,
): Partial<FetchQuoteParams> {
  return type === 'booking-protection' ? bookingProtectionQuoteParams : quoteParams
}

export function getCheckoutInsuranceQuoteParamsByType(
  state: App.State,
  type?: App.InsuranceProductType,
): Partial<FetchQuoteParams> {
  if (type === 'booking-protection') {
    return getCheckoutInsuranceBookingProtectionQuoteParams(state)
  }

  return getCheckoutInsuranceQuoteParams(state)
}

export default getCheckoutInsuranceQuoteParams
